/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\RecordingTtsSection.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Recording Management module.
----------------------------------------------------------------*/


import { Bot, Play, Download } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import Select from '../../../components/ui/select';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TtsState = any;

interface AiProfile {
    id: number | string;
    profile_name: string;
    provider_name: string;
    type: string;
}

interface RecordingTtsSectionProps {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    aiProfiles: any[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    selectedAiProfile: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onProfileChange: (profile: any) => void;
    ttsText: string;
    onTtsTextChange: (text: string) => void;
    startSilenceMs: number;
    endSilenceMs: number;
    onStartSilenceChange: (v: number) => void;
    onEndSilenceChange: (v: number) => void;
    audioUrl: string | null;
    onSetAudioUrl: (url: string) => void;
    isEditMode: boolean;
    tts: TtsState;
    recordingName: string;
    onPlayError: () => void;
    t: (key: string, fallback?: string) => string;
}

export function RecordingTtsSection({
    aiProfiles, selectedAiProfile, onProfileChange, ttsText, onTtsTextChange,
    startSilenceMs, endSilenceMs, onStartSilenceChange, onEndSilenceChange,
    audioUrl, onSetAudioUrl, isEditMode, tts, recordingName, onPlayError, t
}: RecordingTtsSectionProps) {
    return (
        <FormBox title={t('recordingManagement.sections.aiTts')}>
            <div className="space-y-4">
                {/* AI Profile select */}
                <div className="space-y-2">
                    <Label htmlFor="ai_profile">{t('recordingManagement.fields.aiProfile.label')}</Label>
                    <Select
                        value={selectedAiProfile?.id?.toString() || ''}
                        onValueChange={(value) => {
                            const profile = aiProfiles.find((p) => p.id.toString() === value);
                            onProfileChange(profile || null);
                        }}
                        options={aiProfiles.map((profile) => ({
                            value: profile.id.toString(),
                            label: `${profile.profile_name} (${profile.provider_name})`,
                        }))}
                        placeholder={t('recordingManagement.fields.aiProfile.placeholder')}
                    />
                </div>

                {/* Silence padding */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                    <Label htmlFor="start-silence" className="text-left">Start Silence (ms)</Label>
                    <Input
                        id="start-silence"
                        type="number"
                        min={0} max={500} step={10}
                        value={startSilenceMs}
                        onChange={(e) => onStartSilenceChange(Math.max(0, Math.min(500, Number(e.target.value))))}
                    />
                    <Label htmlFor="end-silence" className="text-left">End Silence (ms)</Label>
                    <Input
                        id="end-silence"
                        type="number"
                        min={0} max={500} step={10}
                        value={endSilenceMs}
                        onChange={(e) => onEndSilenceChange(Math.max(0, Math.min(500, Number(e.target.value))))}
                    />
                </div>

                {/* TTS Text */}
                <div className="space-y-2">
                    <Label htmlFor="tts_text" required>{t('recordingManagement.fields.text.label')}</Label>
                    <textarea
                        id="tts_text"
                        value={ttsText}
                        onChange={(e) => onTtsTextChange(e.target.value)}
                        placeholder={t('recordingManagement.fields.text.placeholder')}
                        className="w-full min-h-[120px] px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary resize-y bg-background text-foreground"
                        rows={5}
                    />
                </div>

                {/* Audio Actions */}
                <div className="flex items-center gap-2 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                    <audio
                        ref={(el) => { if (el) (window as any).__ttsAudioRef = el; }}
                        src={audioUrl || undefined}
                        className="hidden"
                        key={audioUrl || 'empty'}
                        onError={() => { if (audioUrl) onPlayError(); }}
                    />
                    <Button
                        type="button"
                        variant="default"
                        onClick={() => selectedAiProfile && tts.generateTTS(ttsText, { ...selectedAiProfile, type: selectedAiProfile.type as 'tts' | 'stt' | 'chat' }, onSetAudioUrl)}
                        disabled={!selectedAiProfile || !ttsText.trim() || tts.isGeneratingTts}
                        className="flex-1 flex items-center justify-center gap-2"
                    >
                        <Bot className="h-4 w-4" />
                        {tts.isGeneratingTts
                            ? t('recordingManagement.generating')
                            : isEditMode
                                ? t('recordingManagement.actions.regenerate')
                                : t('recordingManagement.actions.convertToAudio')}
                    </Button>
                    <Button
                        type="button" variant="outline"
                        onClick={() => { const a = (window as any).__ttsAudioRef as HTMLAudioElement; if (a) a.play(); }}
                        disabled={!audioUrl || tts.isGeneratingTts}
                        className="flex items-center gap-2 border-green-500 text-green-600 hover:bg-green-500/10 hover:text-green-600"
                    >
                        <Play className="h-4 w-4" />
                        {t('recordingManagement.actions.play', 'Play')}
                    </Button>
                    <Button
                        type="button" variant="outline"
                        onClick={() => {
                            if (audioUrl) {
                                const link = document.createElement('a');
                                link.href = audioUrl;
                                link.download = `${recordingName || 'recording'}.wav`;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                            }
                        }}
                        disabled={!audioUrl || tts.isGeneratingTts}
                        className="flex items-center gap-2 border-cyan-500 text-cyan-600 hover:bg-cyan-500/10 hover:text-cyan-600"
                    >
                        <Download className="h-4 w-4" />
                        {t('recordingManagement.actions.download', 'Download')}
                    </Button>
                </div>
            </div>
        </FormBox>
    );
}
